/**
 * Inbox notifications API — eventos del sistema que el admin recibe en el
 * bell del layout. Distinto del módulo /notifications (que son push campaigns
 * que el admin envía a sus usuarios finales).
 *
 * Endpoints (mockeados en mock mode):
 *   GET    /inbox/notifications              → { items, unreadCount }
 *   POST   /inbox/notifications/:id/read     → marca una como leída
 *   POST   /inbox/notifications/read-all     → marca todas como leídas
 *   DELETE /inbox/notifications/:id          → descarta una
 *   POST   /inbox/notifications/clear-all    → vacía el inbox
 */

import { apiClient } from "./client";

export type InboxType =
    | "campaign_sent"
    | "word_blocked"
    | "segment_growing"
    | "whatsapp_warmup"
    | "ban_risk"
    | "template_approved"
    | "ai_suggestion"
    | "system_update"
    | "frequency_cap"
    | "offhours_postponed"
    // ─── Modelo de 3 tipos de campaña ───
    | "campaign_suggested"
    | "campaign_needs_changes"
    | "campaign_approved"
    | "campaign_auto_paused";

export type InboxSeverity = "info" | "success" | "warning" | "critical";

export interface InboxNotification {
    id: string;
    type: InboxType;
    severity: InboxSeverity;
    title: string;
    message: string;
    link?: string;
    createdAt: string;
    read: boolean;
}

export interface InboxListResponse {
    items: InboxNotification[];
    unreadCount: number;
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

export const inboxApi = {
    list: async (): Promise<ApiResponse<InboxListResponse>> => {
        try {
            const r = await apiClient.get<ApiResponse<InboxListResponse>>(
                "/inbox/notifications",
            );
            return r.data;
        } catch (e: any) {
            return { success: false, error: e?.message || "Error al cargar" };
        }
    },

    markRead: async (id: string): Promise<ApiResponse<InboxNotification>> => {
        try {
            const r = await apiClient.post<ApiResponse<InboxNotification>>(
                `/inbox/notifications/${id}/read`,
            );
            return r.data;
        } catch (e: any) {
            return { success: false, error: e?.message || "Error" };
        }
    },

    markAllRead: async (): Promise<ApiResponse<void>> => {
        try {
            const r = await apiClient.post<ApiResponse<void>>(
                "/inbox/notifications/read-all",
            );
            return r.data;
        } catch (e: any) {
            return { success: false, error: e?.message || "Error" };
        }
    },

    dismiss: async (id: string): Promise<ApiResponse<void>> => {
        try {
            const r = await apiClient.delete<ApiResponse<void>>(
                `/inbox/notifications/${id}`,
            );
            return r.data;
        } catch (e: any) {
            return { success: false, error: e?.message || "Error" };
        }
    },

    clearAll: async (): Promise<ApiResponse<void>> => {
        try {
            const r = await apiClient.post<ApiResponse<void>>(
                "/inbox/notifications/clear-all",
            );
            return r.data;
        } catch (e: any) {
            return { success: false, error: e?.message || "Error" };
        }
    },
};
